/**
 * Хелперы для работы с Telegram WebApp API
 */

export function getTelegramWebApp() {
  const webapp = window.Telegram?.WebApp;
  // Проверяем что это реальный Telegram Mini App, а не просто загруженный SDK в браузере
  // В реальном Mini App есть initData, в браузере - нет
  if (webapp && webapp.initData) {
    return webapp;
  }
  return undefined;
}

export function getInitData(): string {
  const webapp = getTelegramWebApp();
  return webapp?.initData || '';
}

export function getUser() {
  const webapp = getTelegramWebApp();
  return webapp?.initDataUnsafe?.user;
}

export function getColorScheme(): 'light' | 'dark' {
  const webapp = getTelegramWebApp();
  return webapp?.colorScheme || 'light';
}

export function hapticFeedback(type: 'success' | 'error' | 'warning' | 'light' | 'medium' | 'heavy') {
  const webapp = getTelegramWebApp();
  if (!webapp?.HapticFeedback) return;

  if (type === 'success' || type === 'error' || type === 'warning') {
    webapp.HapticFeedback.notificationOccurred(type);
  } else {
    webapp.HapticFeedback.impactOccurred(type);
  }
}

let currentMainButtonHandler: (() => void) | null = null;

export function showMainButton(text: string, onClick: () => void) {
  const webapp = getTelegramWebApp();
  if (!webapp?.MainButton) return;

  // Удаляем предыдущий обработчик, если есть
  if (currentMainButtonHandler) {
    webapp.MainButton.offClick(currentMainButtonHandler);
  }

  currentMainButtonHandler = onClick;
  webapp.MainButton.setText(text).onClick(onClick).show();
}

export function hideMainButton() {
  const webapp = getTelegramWebApp();
  if (!webapp?.MainButton) return;

  // Удаляем обработчик при скрытии
  if (currentMainButtonHandler) {
    webapp.MainButton.offClick(currentMainButtonHandler);
    currentMainButtonHandler = null;
  }

  webapp.MainButton.hide();
}

let currentBackButtonHandler: (() => void) | null = null;

export function showBackButton(onClick: () => void) {
  const webapp = getTelegramWebApp();
  if (!webapp?.BackButton) return;

  // Удаляем предыдущий обработчик, если есть
  if (currentBackButtonHandler) {
    webapp.BackButton.offClick(currentBackButtonHandler);
  }

  currentBackButtonHandler = onClick;
  webapp.BackButton.onClick(onClick).show();
}

export function hideBackButton() {
  const webapp = getTelegramWebApp();
  if (!webapp?.BackButton) return;

  // Удаляем обработчик при скрытии
  if (currentBackButtonHandler) {
    webapp.BackButton.offClick(currentBackButtonHandler);
    currentBackButtonHandler = null;
  }

  webapp.BackButton.hide();
}

/**
 * Прокидывает Telegram-овые safe-area инсеты в CSS-переменные.
 * В fullscreen-режиме `env(safe-area-inset-*)` внутри Telegram WebView не отдаёт
 * правильных значений — берём их из webapp.safeAreaInset / contentSafeAreaInset.
 */
function applyTelegramSafeArea(webapp: TelegramWebApp) {
  const root = document.documentElement;
  const sai = webapp.safeAreaInset ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const csai = webapp.contentSafeAreaInset ?? { top: 0, right: 0, bottom: 0, left: 0 };

  const top = (sai.top || 0) + (csai.top || 0);
  const right = (sai.right || 0) + (csai.right || 0);
  const bottom = (sai.bottom || 0) + (csai.bottom || 0);
  const left = (sai.left || 0) + (csai.left || 0);

  root.style.setProperty('--tg-safe-area-top', `${top}px`);
  root.style.setProperty('--tg-safe-area-right', `${right}px`);
  root.style.setProperty('--tg-safe-area-bottom', `${bottom}px`);
  root.style.setProperty('--tg-safe-area-left', `${left}px`);
}

/**
 * Инициализация темы для браузера/PWA (без Telegram WebApp)
 */
export function initBrowserTheme() {
  const root = document.documentElement;
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(isDark ? 'theme-dark' : 'theme-light');

  // Слушаем изменения системной темы
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(e.matches ? 'theme-dark' : 'theme-light');
  });
}

export function initWebApp() {
  const webapp = getTelegramWebApp();
  if (!webapp) {
    console.warn('Telegram WebApp not available');
    initBrowserTheme();
    return;
  }

  webapp.ready();
  webapp.expand();

  try {
    webapp.disableVerticalSwipes?.();
  } catch (err) {
    console.warn('Telegram WebApp vertical swipes not supported', err);
  }

  applyTelegramSafeArea(webapp);
  webapp.onEvent('safeAreaChanged', () => applyTelegramSafeArea(webapp));
  webapp.onEvent('contentSafeAreaChanged', () => applyTelegramSafeArea(webapp));
  webapp.onEvent('fullscreenChanged', () => applyTelegramSafeArea(webapp));
  webapp.onEvent('viewportChanged', () => applyTelegramSafeArea(webapp));

  // Устанавливаем цвета из темы Telegram
  const root = document.documentElement;
  root.style.setProperty('--tg-theme-bg-color', webapp.themeParams.bg_color || '#ffffff');
  root.style.setProperty('--tg-theme-text-color', webapp.themeParams.text_color || '#000000');
  root.style.setProperty('--tg-theme-hint-color', webapp.themeParams.hint_color || '#999999');
  root.style.setProperty('--tg-theme-link-color', webapp.themeParams.link_color || '#2481cc');
  root.style.setProperty('--tg-theme-button-color', webapp.themeParams.button_color || '#2481cc');
  root.style.setProperty('--tg-theme-button-text-color', webapp.themeParams.button_text_color || '#ffffff');
  root.style.setProperty('--tg-theme-secondary-bg-color', webapp.themeParams.secondary_bg_color || '#f0f0f0');

  // Устанавливаем semantic-переменные в зависимости от темы Telegram
  const isDark = webapp.colorScheme === 'dark';
  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(isDark ? 'theme-dark' : 'theme-light');

  if (isDark) {
    root.style.setProperty('--app-surface', '#1c1c1e');
    root.style.setProperty('--app-surface-2', '#2c2c2e');
    root.style.setProperty('--app-border', 'rgba(255, 255, 255, 0.1)');
    root.style.setProperty('--app-shadow', '0 12px 32px rgba(0, 0, 0, 0.35)');
    root.style.setProperty('--app-shadow-soft', '0 4px 12px rgba(0, 0, 0, 0.25)');
    root.style.setProperty('--app-shadow-hover', '0 8px 24px rgba(0, 0, 0, 0.4)');
    root.style.setProperty('--app-card-bg', 'rgba(44, 44, 46, 0.92)');
    root.style.setProperty('--app-input-bg', 'rgba(44, 44, 46, 0.95)');
    root.style.setProperty('--app-overlay-light', 'rgba(28, 28, 30, 0.92)');
    root.style.setProperty('--app-overlay-medium', 'rgba(28, 28, 30, 0.8)');
  } else {
    root.style.setProperty('--app-surface', '#ffffff');
    root.style.setProperty('--app-surface-2', webapp.themeParams.secondary_bg_color || '#f0f0f0');
    root.style.setProperty('--app-border', 'rgba(0, 0, 0, 0.08)');
    root.style.setProperty('--app-shadow', '0 12px 32px rgba(15, 23, 42, 0.10)');
    root.style.setProperty('--app-shadow-soft', '0 4px 12px rgba(15, 23, 42, 0.06)');
    root.style.setProperty('--app-shadow-hover', '0 8px 24px rgba(15, 23, 42, 0.12)');
    root.style.setProperty('--app-card-bg', 'rgba(255, 255, 255, 0.92)');
    root.style.setProperty('--app-input-bg', 'rgba(255, 255, 255, 0.95)');
    root.style.setProperty('--app-overlay-light', 'rgba(255, 255, 255, 0.92)');
    root.style.setProperty('--app-overlay-medium', 'rgba(255, 255, 255, 0.8)');
  }
}
